/**
 * 宗门面板
 */

import type { PlayerState } from '../engine/Player';
import { isSectDisciple, getMissionsAndCheckRealm } from '../engine/SectSystem';
import { SECT_SHOP } from '../data/sectMissions';
import { isBountyExpired, bountyTimeLeft } from '../data/bounties';

export type SectAction = 'submit' | 'refresh' | 'redeem' | 'close';
export interface SectActionPayload { action: SectAction; shopItemId?: string; }

export class SectPanel {
  private overlay: HTMLElement | null = null;
  private onAction: ((payload: SectActionPayload) => void) | null = null;
  private tab: 'missions' | 'shop' = 'missions';

  setOnAction(cb: (p: SectActionPayload) => void): void { this.onAction = cb; }

  open(player: PlayerState): void {
    if (!isSectDisciple(player)) {
      this.showDenied();
      return;
    }
    if (this.overlay) this.overlay.remove();
    const contrib = player.sectContribution ?? 0;
    const missions = getMissionsAndCheckRealm(player);

    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';

    const missionsHtml = missions.length === 0
      ? '<div style="color:var(--text-secondary);text-align:center;padding:16px;">暂无任务，点击刷新获取新任务</div>'
      : missions.map((m) => {
        const prog = m.progress ?? 0;
        const progStr = m.type === 'kill_enemies' ? `${prog}/${m.target}` : '';
        return `
          <div class="recipe-card">
            <div style="font-weight:bold;">📋 ${m.name}</div>
            <div style="font-size:0.8em;color:var(--text-secondary);">${m.desc} ${progStr}</div>
            <div style="font-size:0.8em;">奖励：<span style="color:var(--text-gold);">${m.contributionReward}贡献</span> + ${m.stoneReward}灵石</div>
          </div>
        `;
      }).join('');

    const shopHtml = SECT_SHOP.map((item) => {
      const canBuy = contrib >= item.cost;
      return `
        <div class="recipe-card">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:bold;">${item.name}</span>
            <span style="color:var(--text-gold);">${item.cost}贡献</span>
          </div>
          <div style="font-size:0.75em;color:var(--text-secondary);">${item.desc}</div>
          <button class="btn btn-small ${canBuy ? 'btn-primary' : ''}" data-shop="${item.id}" ${canBuy ? '' : 'disabled'}>
            ${canBuy ? '兑换' : '贡献不足'}
          </button>
        </div>
      `;
    }).join('');

    // 悬赏信息
    let bountyHtml = '';
    if ((player as any).bounty) {
      const b = (player as any).bounty;
      if (!isBountyExpired(b)) {
        bountyHtml = `
          <div style="margin-bottom:8px;padding:8px;background:rgba(200,160,60,0.08);border:1px solid rgba(200,160,60,0.2);border-radius:4px;">
            🏹 <b style="color:var(--text-gold);">悬赏令</b> 📍${b.zoneName} ⏱${bountyTimeLeft(b)}<br>
            <span style="font-size:0.85em;">目标：${b.enemyName} | 💰${b.stoneReward} 📖${b.expReward} 🌿${b.herbReward} 💎${b.coreReward}</span>
          </div>
        `;
      }
    }

    this.overlay.innerHTML = `
      <div class="modal" style="max-width:480px;max-height:80vh;overflow-y:auto;">
        <h2>🏯 宗门</h2>
        ${bountyHtml}
        <div style="margin-bottom:8px;color:var(--text-secondary);">
          贡献度：<b style="color:var(--text-gold);">${contrib}</b>
        </div>
        <div style="display:flex;gap:8px;margin-bottom:12px;">
          <button class="btn btn-small ${this.tab === 'missions' ? 'btn-primary' : ''}" id="sect-tab-missions">📋 任务</button>
          <button class="btn btn-small ${this.tab === 'shop' ? 'btn-primary' : ''}" id="sect-tab-shop">🏪 兑换</button>
        </div>
        <div id="sect-content">
          ${this.tab === 'missions'
            ? missionsHtml + '<div style="display:flex;gap:8px;margin-top:8px;"><button class="btn btn-small btn-primary" id="sect-submit">📤 提交物品</button><button class="btn btn-small" id="sect-refresh">🔄 刷新任务</button></div>'
            : shopHtml}
        </div>
        <button class="btn btn-small" id="sect-close" style="margin-top:12px;">关闭</button>
      </div>
    `;

    document.body.appendChild(this.overlay);

    document.getElementById('sect-tab-missions')?.addEventListener('click', () => { this.tab = 'missions'; this.open(player); });
    document.getElementById('sect-tab-shop')?.addEventListener('click', () => { this.tab = 'shop'; this.open(player); });
    document.getElementById('sect-submit')?.addEventListener('click', () => { if (this.onAction) this.onAction({ action: 'submit' }); });
    document.getElementById('sect-refresh')?.addEventListener('click', () => { if (this.onAction) this.onAction({ action: 'refresh' }); this.open(player); });
    document.getElementById('sect-close')?.addEventListener('click', () => { this.close(); if (this.onAction) this.onAction({ action: 'close' }); });
    this.overlay.querySelectorAll('button[data-shop]').forEach((b) => {
      b.addEventListener('click', () => {
        const id = (b as HTMLElement).dataset.shop!;
        if (this.onAction) this.onAction({ action: 'redeem', shopItemId: id });
      });
    });
    this.overlay.addEventListener('click', (e) => { if (e.target === this.overlay) { this.close(); if (this.onAction) this.onAction({ action: 'close' }); } });
  }

  private showDenied(): void {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `<div class="modal" style="max-width:340px;text-align:center;"><h2>🏯 宗门</h2><p>只有门派弟子才能使用宗门功能。</p><p style="color:var(--text-secondary);">散修可在金丹期后寻找门派加入。</p><button class="btn btn-small" id="sect-deny-close">知道了</button></div>`;
    document.body.appendChild(overlay);
    document.getElementById('sect-deny-close')?.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  }

  close(): void { if (this.overlay) { this.overlay.remove(); this.overlay = null; } }
  isOpen(): boolean { return this.overlay !== null; }
}
